<template>
  <div>
    <EquipmentEditHeader
      v-if="isAdmin"
      data-cy="switchboard-search-view-edit-header"
    />

    <div v-else class="mb-3">
      <div class="flex align-items-center gap-4">
        <div v-if="isHps" class="flex align-items-center field-checkbox">
          <Checkbox
            v-model="enableFilter"
            input-id="enable-switchboard-filter"
            binary
          />
          <label for="enable-switchboard-filter">
            Filter and Sort Surface Drive
          </label>
        </div>
      </div>
    </div>

    <div
      v-if="noMotorForFilter"
      class="no-recommendation-message-container"
      data-cy="switchboard-no-motor-message"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No surface drive recommendations can be made.
        </p>
        <p class="no-recommendation-body">
          Please select a motor to see surface drive/switchboard
          recommendations, or un-check
          <span class="no-recommendation-highlight">
            "Filter and Sort Surface Drive"
          </span>
          to view all options.
        </p>
      </div>
    </div>

    <div
      v-else-if="noSwitchboardsWithFilter"
      class="no-recommendation-message-container"
      data-cy="switchboard-no-results-message"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No surface drives/switchboards match this configuration.
        </p>
        <p class="no-recommendation-body">
          If you'd like to select a surface drive or switchboard, please
          un-check
          <span class="no-recommendation-highlight">
            "Filter and Sort Surface Drive"
          </span>
          to view all available options.
        </p>
      </div>
    </div>

    <EquipmentTable
      v-else
      data-cy="switchboard-search-view-table"
      @select="$emit('select', $event.data as Switchboards)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, inject, watch } from "vue";
import Checkbox from "primevue/checkbox";
import { useSwitchboards } from "@/composables/equipment/switchboards/switchboards";
import EquipmentEditHeader from "@/components/equipment/EquipmentEditHeader.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import type { Switchboards } from "gql/api";
import {
  equipmentFormKey,
  scenarioCalculationsKey,
  scenarioFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import { switchboardKvFieldToVolts } from "@/libs/units/voltage";

const props = defineProps<{
  isAdmin: boolean;
}>();

defineEmits<{
  (event: "select", data: Switchboards): void;
}>();

const switchboardsearch = useSwitchboards(props.isAdmin);

const scenarioForm = !props.isAdmin ? inject(scenarioFormKey, null) : null;
const scenarioCalculations = !props.isAdmin
  ? inject(scenarioCalculationsKey, null)
  : null;
const equipmentForm = !props.isAdmin ? inject(equipmentFormKey, null) : null;

const isHps = computed(() => {
  if (!scenarioForm) return false;
  return scenarioForm.data.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

const enableFilter = ref<boolean>(isHps.value);

watch(
  isHps,
  value => {
    if (value && !enableFilter.value) {
      enableFilter.value = true;
    }
  },
  { immediate: false }
);

const isMotorSelected = computed(() => {
  if (!equipmentForm) return false;
  const eq = equipmentForm.data.value;
  return !!(eq.motor_id || eq.lower_motor_id);
});

const sortedSwitchboards = computed(() => {
  const rows = switchboardsearch.allRows.value;

  // Preserve undefined while loading so the table can show its loading state.
  if (!rows) {
    return rows;
  }

  // For admin views or non-HPS sizings, or when Filter & Sort is disabled,
  // keep existing behavior.
  if (props.isAdmin || !isHps.value || !enableFilter.value) {
    return rows;
  }

  // If there is no motor selected, we can't make a recommendation.
  if (!isMotorSelected.value) {
    return [];
  }

  if (!scenarioCalculations) {
    return rows;
  }

  // Catalog voltage (from `kv`) must meet line requirement; amp rating uses
  // downhole motor rerated amps (not surface maxAmpRatingA after transformer ratio).

  const surfaceDriveCalcs = scenarioCalculations.surfaceDriveCalculations;
  const motorCalcs = scenarioCalculations.motorCalculations;
  const requiredVoltage = surfaceDriveCalcs.requiredVoltageV.value;
  const requiredAmps = motorCalcs.reratedNameplateA.value;
  const motorLineVolts =
    motorCalcs.reratedNameplateV.value || motorCalcs.voltageNameplate.value;
  const voltageRequirement = Math.max(
    requiredVoltage || 0,
    motorLineVolts || 0
  );

  if (!voltageRequirement || !requiredAmps) {
    return rows;
  }

  const passing: Array<{
    row: Switchboards;
    volts: number;
    amps: number;
  }> = [];

  rows.forEach(row => {
    const kv = row.kv;
    const amps = row.amps;

    if (
      kv === null ||
      kv === undefined ||
      amps === null ||
      amps === undefined
    ) {
      return;
    }

    const volts = switchboardKvFieldToVolts(Number(kv));
    const rowAmps = Number(amps);

    if (volts >= voltageRequirement && rowAmps >= requiredAmps) {
      passing.push({ row, volts, amps: rowAmps });
    }
  });

  if (passing.length === 0) {
    return [];
  }

  // Sort passing rows by “best fit”: smallest voltage that still meets the
  // requirement, then smallest amp rating.
  passing.sort((a, b) => {
    if (a.volts !== b.volts) {
      return a.volts - b.volts;
    }
    return a.amps - b.amps;
  });

  return passing.map(entry => entry.row);
});

const sortedFilteredRows = computed(() => {
  const baseFiltered = switchboardsearch.filteredRows.value;
  const sorted = sortedSwitchboards.value;

  // Preserve undefined while loading so the table can show loading state.
  if (!baseFiltered || !sorted) {
    return sorted as typeof baseFiltered;
  }

  // Always respect the composable's filteredRows (which already apply
  // showDeleted / customer filters and search), then apply our
  // recommendation ordering on top of that.
  const filteredIds = new Set(baseFiltered.map(p => p.id));
  return sorted.filter(switchboard => filteredIds.has(switchboard.id));
});

const noMotorForFilter = computed(() => {
  const filterOn = enableFilter.value && isHps.value && !props.isAdmin;
  const hasLoadedRows = Array.isArray(switchboardsearch.allRows.value);
  return filterOn && hasLoadedRows && !isMotorSelected.value;
});

const noSwitchboardsWithFilter = computed(() => {
  const filterOn = enableFilter.value && isHps.value && !props.isAdmin;
  const count = sortedFilteredRows.value?.length ?? 0;
  const hasLoadedRows = Array.isArray(switchboardsearch.allRows.value);
  return filterOn && hasLoadedRows && count === 0 && isMotorSelected.value;
});

const equipmentWithSortedSwitchboards = {
  ...switchboardsearch,
  allRows: sortedSwitchboards,
  filteredRows: sortedFilteredRows
};

provide("equipment", equipmentWithSortedSwitchboards);
</script>
